// src/context/UIContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode
} from 'react'
import { useTheme, useMediaQuery } from '@mui/material'

interface UIContextType {
  isMobile: boolean
  viewMode: 'short' | 'full'
  isShortMode: boolean
  isCollapsed: boolean
  toggleCollapsed: () => void
  toggleMode: () => void
  resetMode: () => void
  isVisible: (visibility?: 'mobile' | 'desktop' | 'both') => boolean
}

const UIContext = createContext<UIContextType | undefined>(undefined)

export const UIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const theme = useTheme()
  // Detectamos si es pantalla pequeña
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Leemos si el usuario ya tenía una preferencia guardada, sino, por defecto 'short'
  const [viewMode, setViewMode] = useState<'short' | 'full'>(() => {
    const saved = localStorage.getItem('wms_view_mode')
    return saved === 'full' || saved === 'short' ? saved : 'short'
  })

  // Guardamos la preferencia en el navegador cada vez que cambie
  useEffect(() => {
    localStorage.setItem('wms_view_mode', viewMode)
  }, [viewMode])

  // Estado del menú lateral colapsado (solo iconos). Aplica únicamente en desktop.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('wms_menu_collapsed') === 'true'
  })

  useEffect(() => {
    localStorage.setItem('wms_menu_collapsed', String(collapsed))
  }, [collapsed])

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev)
  }

  const toggleMode = () => {
    setViewMode((prev) => (prev === 'short' ? 'full' : 'short'))
  }

  const resetMode = () => {
    setViewMode('short')
  }

  const isVisible = (visibility: 'mobile' | 'desktop' | 'both' = 'desktop') => {
    if (visibility === 'both') return true
    if (visibility === 'mobile') return isMobile
    if (visibility === 'desktop') return !isMobile
    return true
  }

  return (
    <UIContext.Provider
      value={{
        isMobile,
        viewMode,
        // Solo aplica el modo corto si está en un celular Y eligió el modo corto
        isShortMode: isMobile && viewMode === 'short',
        // El colapso a solo iconos solo aplica en desktop
        isCollapsed: !isMobile && collapsed,
        toggleCollapsed,
        toggleMode,
        resetMode,
        isVisible
      }}
    >
      {children}
    </UIContext.Provider>
  )
}

// Hook personalizado para usar en cualquier componente
export const useUIMode = () => {
  const context = useContext(UIContext)
  if (!context) {
    throw new Error('useUIMode debe ser usado dentro de un UIProvider')
  }
  return context
}
